// =============================================================================
// SIREPPAR - Wizard de AUTORIDADES DEFINITIVAS del reconocimiento
// =============================================================================
// Verificado el padrón de afiliados, el partido realiza sus elecciones internas y
// carga acá —en un solo asistente y solo en la fase AUTORIDADES_PENDIENTES:
//   1. La documentación de la etapa (convocatoria, cronograma, asamblea,
//      proclamación y designación de apoderados). Las fichas de afiliación NO se
//      suben: se entregan en papel y acá solo se descarga el modelo.
//   2. Los órganos de conducción con sus autoridades electas (cargo obligatorio,
//      titular/suplente, mandato desde/hasta por órgano).
//   3. Los apoderados designados.
//   4. Los datos del partido que faltaban (domicilio legal, contacto, redes y la
//      fecha de la última elección interna).
//
// Al enviarse, el backend retira a las autoridades y apoderados PROMOTORES a la
// nómina histórica (conservando ese título) y deja vigentes a estas definitivas.
//
// Mismo look & feel y mismos mecanismos que el wizard de documentación
// (ReconocimientoDocumentacionPage): validación de DNI en vivo contra el padrón,
// reutilización de DNI ya verificados y subida a S3 en un solo lote.
// Las reglas de cargos por órgano son las del wizard de reempadronamiento.
// =============================================================================

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api, reconocimientoService, documentoService, partidoService } from '../../services/api';
import { useReconocimientoApoderado } from '../../hooks/useReconocimientoApoderado';
import { useLocalidades } from '../../hooks/useLocalidades';
import { useAuth } from '../../contexts/AuthContext';
import { TIPOS_SOLO_EXCEL, acceptParaTipo, esArchivoExcelOds, msgSoloExcel } from '../../utils/tiposDocumento';
import {
  TIPO_ORGANO_CERTIFICADORES,
  CARGO_CERTIFICADOR_FIRMAS,
  LABEL_CERTIFICADORES,
  LABEL_CARGO_CERTIFICADOR,
  MAX_CERTIFICADORES,
  esSeccionCertificadores,
  vocabularioOrgano,
} from '../../utils/certificadoresFirmas';

// Una fila de la lista de documentación de la etapa.
//   plantilla → modelo descargable, servido por el backend desde docs/ (la MISMA
//     fuente que los adjuntos del correo).
//   sinCarga  → la pieza NO se sube al sistema; se entrega en papel o por correo. En
//     lugar del botón "Adjuntar" se muestra la vía de entrega, y la fila no cuenta
//     para el gate de "documentación completa".
interface FilaDoc {
  tipo: string;
  label: string;
  ayuda: string;
  plantilla?: { url: string; filename: string };
  sinCarga?: boolean;
}

// Documentación de la etapa. Se guarda con tipos del enum TipoDocumento: la
// Resolución de la Proclamación usa CERTIFICADO_AUTORIDADES; la publicación del
// llamado y el cronograma tienen tipos propios (ver la migración
// 20260814100000_add_docs_proceso_electoral_interno).
//
// El orden sigue el del proceso electoral interno: primero cómo se convocó
// (publicación + cronograma), después qué resultó (asamblea + proclamación).
const DOCS_ETAPA: FilaDoc[] = [
  {
    tipo: 'PUBLICACION_LLAMADO_ELECCIONES',
    label: 'Publicación de llamado a elecciones',
    ayuda: 'Publicación con la que se convocó a las elecciones internas.',
  },
  {
    tipo: 'CRONOGRAMA_ELECCIONES',
    label: 'Cronograma de elecciones',
    ayuda: 'Calendario del proceso electoral interno.',
  },
  {
    tipo: 'ACTA_ASAMBLEA',
    label: 'Acta de asamblea donde se proclamaron las autoridades',
    ayuda: 'Donde conste la elección de las autoridades partidarias.',
  },
  {
    tipo: 'CERTIFICADO_AUTORIDADES',
    label: 'Resolución de la Proclamación de Autoridades',
    ayuda: 'Resolución firmada y escaneada donde constan las autoridades electas.',
  },
  {
    tipo: 'ACTA_DESIGNACION_APODERADOS',
    label: 'Acta de Designación de Apoderados',
    ayuda: 'Instrumento por el que el partido designa a sus apoderados.',
  },
  // Las FICHAS de afiliación (Art. 17) no se suben: son una por afiliado, firmadas de
  // puño, y se entregan en papel. Acá solo se ofrece el modelo para imprimir.
  {
    tipo: 'FICHAS_AFILIACION_PAPEL',
    label: 'Todas las fichas de afiliacion',
    ayuda: 'Una ficha por afiliado, firmada. Deberán presentarse presencialmente en la Secretaria Electoral o mediante Correo electronico a registro@electoralchubut.gob.ar.',
    sinCarga: true,
    plantilla: { url: '/reconocimiento/ficha-afiliacion', filename: 'Ficha de Afiliacion.pdf' },
  },
];

// Filas que SÍ exigen archivo: es el gate real de "documentación completa".
const DOCS_A_CARGAR = DOCS_ETAPA.filter((d) => !d.sinCarga);

// Los CERTIFICADORES van al final de los obligatorios: no son un órgano de
// conducción, son la sección de quienes avalan las fichas y los avales. Se modelan
// como órgano para no duplicar la carga de personas ni el gate de DNI (ver
// utils/certificadoresFirmas.ts).
const ORGANOS_OBLIGATORIOS = [
  { tipo: 'CONGRESO_CONVENCION', label: 'Congreso o Convención' },
  { tipo: 'COMITE_JUNTA_CONSEJO', label: 'Comité, Junta o Consejo' },
  { tipo: 'JUNTA_ELECTORAL', label: 'Junta Electoral' },
  { tipo: TIPO_ORGANO_CERTIFICADORES, label: LABEL_CERTIFICADORES },
];
const ORGANOS_OPCIONALES = [
  { tipo: 'MESA_DIRECTIVA', label: 'Mesa Directiva' },
  { tipo: 'TRIBUNAL_CONDUCTA', label: 'Tribunal de Conducta/Disciplina' },
  { tipo: 'JUNTA_DEPARTAMENTAL', label: 'Junta Departamental/Seccional' },
  { tipo: 'OTRO', label: 'Otro (especificar)' },
];
const orgLabel = (t: string) =>
  [...ORGANOS_OBLIGATORIOS, ...ORGANOS_OPCIONALES].find((o) => o.tipo === t)?.label || t;

// A diferencia de las promotoras, acá el cargo es obligatorio: sale de la
// proclamación de las autoridades electas.
const CARGOS = ['PRESIDENTE', 'VICEPRESIDENTE', 'SECRETARIO', 'PROSECRETARIO', 'TESORERO', 'PROTESORERO', 'VOCAL', 'OTRO'];
const cargoLabel = (c: string) => c.charAt(0) + c.slice(1).toLowerCase();

// Cargos ofrecidos en un bloque. En la sección de certificadores hay uno solo.
const cargosDeOrgano = (tipoOrgano: string): string[] =>
  esSeccionCertificadores(tipoOrgano) ? [CARGO_CERTIFICADOR_FIRMAS] : CARGOS;
const etiquetaCargo = (c: string): string =>
  c === CARGO_CERTIFICADOR_FIRMAS ? LABEL_CARGO_CERTIFICADOR
    : c === 'OTRO' ? 'Otro (especificar)'
      : cargoLabel(c);

const GENEROS = [
  { value: 'FEMENINO', label: 'Femenino' },
  { value: 'MASCULINO', label: 'Masculino' },
  { value: 'NO_BINARIO', label: 'No binario' },
  { value: 'OTRO', label: 'Otro' },
  { value: 'NO_ESPECIFICA', label: 'Prefiero no especificar' },
];
const GENEROS_MANUAL = [
  { value: 'FEMENINO', label: 'Femenino' },
  { value: 'MASCULINO', label: 'Masculino' },
  { value: 'OTRO', label: 'Otro' },
];
const generoLabel = (v?: string) => GENEROS.find((g) => g.value === v)?.label || '—';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const soloLetras = (v: string) => v.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]/g, '');

// Los datos del partido van en SEGUNDO lugar: son campos cortos del partido en sí
// (domicilio legal, email oficial), no dependen de nada cargado después, y dejarlos
// al final los ponía detrás de los dos pasos más largos del wizard.
const pasos = ['Documentos', 'Datos del partido', 'Órganos y autoridades', 'Apoderados', 'Confirmar'];

interface PersonaDni {
  key: string;
  dni: string;
  nombre: string;
  apellido: string;
  genero: string;
  estado: 'idle' | 'loading' | 'ok' | 'error';
  mensaje?: string;
  dniExisten?: boolean;
  dniFrentePath?: string;
  dniDorsoPath?: string;
  dniFrenteFile?: File | null;
  dniDorsoFile?: File | null;
  enPadron?: boolean; // true → datos precargados del padrón (solo lectura)
}
interface AutoridadElecta extends PersonaDni {
  cargo: string;
  cargoPersonalizado: string; // solo cuando cargo === 'OTRO'
  esTitular: boolean;
}
interface OrganoDefinitivo {
  id: string;
  tipoOrgano: string;
  denominacionPersonalizada: string;
  esObligatorio: boolean;
  mandatoDesde: string;
  mandatoHasta: string;
  autoridades: AutoridadElecta[];
}

let _seq = 0;
const nuevoId = () => `${Date.now()}-${_seq++}`;
const nuevaPersona = (): PersonaDni => ({ key: nuevoId(), dni: '', nombre: '', apellido: '', genero: '', estado: 'idle' });
const nuevaAutoridad = (): AutoridadElecta => ({ ...nuevaPersona(), cargo: '', cargoPersonalizado: '', esTitular: true });

/** Cargo efectivo (resuelve la opción "OTRO"). */
const cargoEfectivo = (a: AutoridadElecta) =>
  (a.cargo === 'OTRO' ? a.cargoPersonalizado : a.cargo).trim();

// Valida un DNI contra el padrón (endpoint AUTENTICADO) y detecta si ya tiene un
// DNI verificado cargado, para reutilizarlo. Idéntico al wizard de documentación.
async function resolverDni(limpio: string): Promise<Partial<PersonaDni> & { ok: boolean }> {
  const r = await api.get(`/padrones/validar-dni-privado/${limpio}`);
  const data = r.data?.data;
  if (!data?.valido) {
    return { ok: false, estado: 'error', mensaje: data?.mensaje || 'DNI no encontrado en el padrón' };
  }
  let dniExisten = false;
  let dniFrentePath: string | undefined;
  let dniDorsoPath: string | undefined;
  try {
    const c = await documentoService.checkDniPersona(limpio);
    const d = c.data?.data;
    if (d?.frente && d?.dorso) {
      dniExisten = true;
      dniFrentePath = d.frente.path;
      dniDorsoPath = d.dorso.path;
    }
  } catch { /* sin documento previo: se pedirá subir */ }
  return {
    ok: true,
    estado: 'ok',
    mensaje: undefined,
    nombre: data.datos?.nombre ? soloLetras(data.datos.nombre) : '',
    apellido: data.datos?.apellido ? soloLetras(data.datos.apellido) : '',
    genero: data.datos?.genero || 'OTRO',
    dniExisten,
    dniFrentePath,
    dniDorsoPath,
  };
}

const personaDniIncompleta = (p: PersonaDni) =>
  !p.dni || !p.nombre || !p.apellido || p.estado !== 'ok' || (!p.dniExisten && (!p.dniFrenteFile || !p.dniDorsoFile));

export default function ReconocimientoAutoridadesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const recon = useReconocimientoApoderado();
  const { user } = useAuth();
  const { localidades } = useLocalidades();

  const [pasoActual, setPasoActual] = useState(0);
  const [docs, setDocs] = useState<Record<string, File | null>>({});
  const [organos, setOrganos] = useState<OrganoDefinitivo[]>(
    ORGANOS_OBLIGATORIOS.map((o) => ({
      id: nuevoId(),
      tipoOrgano: o.tipo,
      // La sección de certificadores ya viene con su nombre: no se le pide al partido
      // que la denomine (no es un órgano de la carta orgánica).
      denominacionPersonalizada: esSeccionCertificadores(o.tipo) ? LABEL_CERTIFICADORES : '',
      esObligatorio: true,
      mandatoDesde: '',
      mandatoHasta: '',
      autoridades: [],
    })),
  );
  const [apoderados, setApoderados] = useState<PersonaDni[]>([]);
  const [datosPartido, setDatosPartido] = useState({
    domicilioLegal: '',
    emailOficial: '',
    localidad: '',
    telefonoOficial: '',
    sitioWeb: '',
    redesSociales: '',
    fechaUltimaEleccionInterna: '',
  });
  const [loading, setLoading] = useState(false);
  const [subiendo, setSubiendo] = useState<{ current: number; total: number } | null>(null);
  // Precarga desde la nómina actual del partido (autoridades/apoderados
  // promotores). Se pide UNA sola vez y se cachea acá.
  const [nomina, setNomina] = useState<{ autoridades: any[]; apoderados: any[] } | null>(null);
  const [precargando, setPrecargando] = useState<'aut' | 'apod' | null>(null);

  // Tipo y localidad que YA tiene el partido en el sistema. Definen si la localidad
  // se pide o se muestra como dato fijo (ver `localidadFija` más abajo).
  const [partidoRegistrado, setPartidoRegistrado] = useState<{ tipo?: string; localidad?: string } | null>(null);

  // Precarga de los datos del partido que YA están cargados. Un partido municipal
  // declaró su localidad al crearse el expediente; uno provincial no (el formulario
  // de constitución no la pide). Sin esta precarga, el municipal tendría que volver
  // a elegirla y podría cambiarla por error.
  useEffect(() => {
    if (!user?.partidoId) return;
    let cancelado = false;
    partidoService.getById(user.partidoId)
      .then((r) => {
        const p = r.data?.data;
        if (!p || cancelado) return;
        setPartidoRegistrado({ tipo: p.tipo, localidad: p.localidad || '' });
        setDatosPartido((prev) => ({
          ...prev,
          // Solo se rellena lo que el usuario todavía no tocó.
          domicilioLegal: prev.domicilioLegal || p.domicilioLegal || '',
          emailOficial: prev.emailOficial || p.emailOficial || '',
          localidad: prev.localidad || p.localidad || '',
          telefonoOficial: prev.telefonoOficial || p.telefonoOficial || '',
          sitioWeb: prev.sitioWeb || p.sitioWeb || '',
        }));
      })
      .catch(() => { /* no bloquea el wizard: se cargan a mano */ });
    return () => { cancelado = true; };
  }, [user?.partidoId]);

  // Guarda: si el trámite no está en la etapa correcta, no hay wizard que mostrar.
  useEffect(() => {
    if (!recon.isLoading && !recon.mostrarWizardAutoridades) {
      navigate('/dashboard', { replace: true });
    }
  }, [recon.isLoading, recon.mostrarWizardAutoridades, navigate]);

  // La localidad de un partido MUNICIPAL no se pregunta: la declaró al iniciar el
  // trámite (wizard de constitución) y define su ámbito — es el "Partido Político
  // Municipal de la ciudad de X" que sale publicado en el edicto del Art. 10. Dejarla
  // editable acá permitía cambiarla por error y contradecir lo ya publicado, así que
  // se muestra como dato fijo.
  //
  // Un partido PROVINCIAL sí la elige: su ámbito es toda la provincia, y acá lo que
  // se declara es la localidad del DOMICILIO LEGAL, que no está cargada de antes.
  //
  // Si un municipal no la tuviera cargada (dato viejo o incompleto), se cae al select
  // en lugar de dejarlo sin poder completar el paso.
  const localidadFija =
    partidoRegistrado?.tipo === 'MUNICIPAL' && !!partidoRegistrado.localidad?.trim()
      ? partidoRegistrado.localidad.trim()
      : null;

  // ---- Documentos ----
  const setDoc = (tipo: string, file: File | null) => {
    // Ningún documento de esta etapa admite planilla de cálculo; la validación por
    // tipo la centraliza acceptParaTipo/esArchivoExcelOds, igual que el otro wizard.
    if (file && TIPOS_SOLO_EXCEL.includes(tipo) && !esArchivoExcelOds(file.name)) {
      toast.error(msgSoloExcel(tipo));
      return;
    }
    setDocs((p) => ({ ...p, [tipo]: file }));
  };
  const docsCompletos = DOCS_A_CARGAR.every((d) => !!docs[d.tipo]);

  // Las plantillas se sirven desde el backend (misma fuente que los adjuntos del
  // correo) y esos endpoints piden token: por eso se bajan como blob en lugar de un
  // <a href download>, que iría sin la cabecera de autorización.
  const descargarPlantilla = async (endpoint: string, filename: string) => {
    try {
      const res = await api.get(endpoint, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      toast.error(`No se pudo descargar ${filename}`);
    }
  };

  // ---- Órganos y autoridades ----
  const agregarOrgano = (tipo: string) =>
    setOrganos((p) => [
      ...p,
      { id: nuevoId(), tipoOrgano: tipo, denominacionPersonalizada: '', esObligatorio: false, mandatoDesde: '', mandatoHasta: '', autoridades: [] },
    ]);
  const eliminarOrgano = (id: string) => setOrganos((p) => p.filter((o) => o.id !== id));
  const updateOrgano = (id: string, campo: 'denominacionPersonalizada' | 'mandatoDesde' | 'mandatoHasta', valor: string) =>
    setOrganos((p) => p.map((o) => (o.id === id ? { ...o, [campo]: valor } : o)));
  // En la sección de certificadores hay un solo cargo posible y no existe el
  // suplente, así que la persona nueva ya viene con el cargo puesto y como titular:
  // no tiene sentido hacer elegir en un desplegable de una sola opción.
  const agregarAutoridad = (organoId: string) =>
    setOrganos((p) =>
      p.map((o) => {
        if (o.id !== organoId) return o;
        if (esSeccionCertificadores(o.tipoOrgano) && o.autoridades.length >= MAX_CERTIFICADORES) {
          toast.error(`Podés cargar hasta ${MAX_CERTIFICADORES} certificadores.`);
          return o;
        }
        const nueva = esSeccionCertificadores(o.tipoOrgano)
          ? { ...nuevaAutoridad(), cargo: CARGO_CERTIFICADOR_FIRMAS, esTitular: true }
          : nuevaAutoridad();
        return { ...o, autoridades: [...o.autoridades, nueva] };
      }),
    );
  const eliminarAutoridad = (organoId: string, key: string) =>
    setOrganos((p) => p.map((o) => (o.id === organoId ? { ...o, autoridades: o.autoridades.filter((x) => x.key !== key) } : o)));
  const updateAutoridad = (organoId: string, key: string, patch: Partial<AutoridadElecta>) =>
    setOrganos((p) =>
      p.map((o) => (o.id === organoId ? { ...o, autoridades: o.autoridades.map((x) => (x.key === key ? { ...x, ...patch } : x)) } : o)),
    );
  const validarDniAutoridad = async (organoId: string, key: string, dni: string) => {
    const limpio = dni.replace(/\D/g, '');
    if (limpio.length < 7) { updateAutoridad(organoId, key, { estado: 'idle', mensaje: undefined }); return; }
    updateAutoridad(organoId, key, { estado: 'loading' });
    try { updateAutoridad(organoId, key, await resolverDni(limpio) as Partial<AutoridadElecta>); }
    catch { updateAutoridad(organoId, key, { estado: 'error', mensaje: 'No se pudo validar el DNI' }); }
  };

  // Reglas de cargos por órgano (mismas que el wizard de reempadronamiento):
  //  - PRESIDENTE: un solo titular y SIN suplente.
  //  - VICEPRESIDENTE: un solo titular, puede tener suplente.
  //  - Los demás cargos: libres (varios titulares y suplentes).
  //  - CERTIFICADOR_FIRMAS: varios titulares (hasta MAX_CERTIFICADORES), sin suplentes.
  const cargoYaExisteEnOrgano = (organoId: string, cargo: string, key: string, esTitularActual: boolean): boolean => {
    const org = organos.find((o) => o.id === organoId);
    if (!org) return false;
    // Puede haber hasta 3 certificadores, todos con el mismo cargo; lo único que no
    // existe es el certificador suplente.
    if (cargo === CARGO_CERTIFICADOR_FIRMAS) return !esTitularActual;
    if (cargo === 'PRESIDENTE') {
      if (!esTitularActual) return true; // el presidente no tiene suplente
      return org.autoridades.some((a) => a.key !== key && a.esTitular && a.cargo === 'PRESIDENTE');
    }
    if (cargo === 'VICEPRESIDENTE') {
      if (!esTitularActual) return false; // el vicepresidente puede tener suplente
      return org.autoridades.some((a) => a.key !== key && a.esTitular && a.cargo === 'VICEPRESIDENTE');
    }
    return false;
  };

  // ---- Apoderados ----
  const agregarApoderado = () => setApoderados((p) => [...p, nuevaPersona()]);
  const eliminarApoderado = (key: string) => setApoderados((p) => p.filter((a) => a.key !== key));
  const updateApoderado = (key: string, patch: Partial<PersonaDni>) =>
    setApoderados((p) => p.map((a) => (a.key === key ? { ...a, ...patch } : a)));
  // Apoderados: NO requieren estar en el padrón (Ley XII-23 Art. 17/18; Cód.
  // Electoral Art. 62). Si ESTÁN, se precargan sus datos (solo lectura); si no,
  // se cargan a mano (género limitado). Nunca bloquea por padrón.
  const chequearDniApoderado = async (key: string, dni: string) => {
    const limpio = dni.replace(/\D/g, '');
    if (limpio.length < 7) { updateApoderado(key, { estado: 'idle', mensaje: undefined, enPadron: undefined, dniExisten: undefined }); return; }
    updateApoderado(key, { estado: 'loading' });
    const patch: Partial<PersonaDni> = { estado: 'ok', mensaje: undefined, enPadron: false, dniExisten: false };
    try {
      const r = await api.get(`/padrones/validar-dni-privado/${limpio}`);
      const d = r.data?.data;
      if (d?.valido) {
        patch.enPadron = true;
        patch.nombre = d.datos?.nombre ? soloLetras(d.datos.nombre) : '';
        patch.apellido = d.datos?.apellido ? soloLetras(d.datos.apellido) : '';
        patch.genero = d.datos?.genero || '';
      }
    } catch { /* padrón no disponible: se sigue con carga manual */ }
    try {
      const c = await documentoService.checkDniPersona(limpio);
      const dd = c.data?.data;
      if (dd?.frente && dd?.dorso) { patch.dniExisten = true; patch.dniFrentePath = dd.frente.path; patch.dniDorsoPath = dd.dorso.path; }
    } catch { /* sin documento previo */ }
    updateApoderado(key, patch);
  };

  // ---------------------------------------------------------------------------
  // Precarga desde la nómina actual (autoridades/apoderados promotores)
  // ---------------------------------------------------------------------------
  // En la práctica muchas de las autoridades promotoras vuelven a ser electas, así
  // que reescribir todos los DNI a mano es puro trabajo repetido. Con una sola
  // llamada (GET /partidos/:id/autoridades) se traen las dos nóminas; los DNI se
  // resuelven después en paralelo, exactamente igual que si se los tipeara.
  const cargarNomina = async () => {
    if (nomina) return nomina;
    if (!user?.partidoId) return null;
    const r = await partidoService.getAutoridades(user.partidoId);
    const data = { autoridades: r.data?.data?.autoridades ?? [], apoderados: r.data?.data?.apoderados ?? [] };
    setNomina(data);
    return data;
  };

  /** Resuelve en paralelo los DNI de una lista de personas ya precargadas. */
  const resolverDnisEnLote = async <T extends PersonaDni>(personas: T[]): Promise<T[]> =>
    Promise.all(
      personas.map(async (p) => {
        try {
          const r = await resolverDni(p.dni);
          // Nombre/apellido/género del padrón son la fuente; si el DNI no está en
          // el padrón, se conserva lo que vino de la nómina y se marca el error.
          return r.ok ? { ...p, ...r } as T : { ...p, estado: 'error', mensaje: r.mensaje } as T;
        } catch {
          return { ...p, estado: 'error', mensaje: 'No se pudo validar el DNI' } as T;
        }
      }),
    );

  const usarUltimasAutoridades = async () => {
    setPrecargando('aut');
    try {
      const data = await cargarNomina();
      if (!data) return;
      // Nómina vigente: autoridades con mandato activo (las promotoras, en esta
      // etapa). Se agrupan por el órgano al que pertenecían.
      const vigentes = data.autoridades.filter((a: any) => a.mandatos?.[0]?.activo === true);
      if (vigentes.length === 0) { toast.error('No hay autoridades cargadas para reutilizar.'); return; }

      const porOrgano = new Map<string, any[]>();
      for (const a of vigentes) {
        const tipo = a.organo?.tipoOrgano || 'OTRO';
        porOrgano.set(tipo, [...(porOrgano.get(tipo) ?? []), a]);
      }

      const nuevos: OrganoDefinitivo[] = [];
      for (const [tipoOrgano, lista] of porOrgano) {
        const esObligatorio = ORGANOS_OBLIGATORIOS.some((o) => o.tipo === tipoOrgano);
        const esCert = esSeccionCertificadores(tipoOrgano);
        // En certificadores el cargo es único y no hay suplentes, así que se hereda
        // siempre; el tope de 3 se aplica acá para que la precarga no lo rompa.
        const personasFuente = esCert ? lista.slice(0, MAX_CERTIFICADORES) : lista;
        const personas = personasFuente.map((a: any) => {
          const cargoPrev = (a.mandatos?.[0]?.cargo || '').toUpperCase();
          const esCargoConocido = CARGOS.includes(cargoPrev);
          return {
            ...nuevaPersona(),
            dni: String(a.dni || '').replace(/\D/g, ''),
            nombre: a.nombre || '',
            apellido: a.apellido || '',
            genero: a.genero || '',
            // El cargo NO se hereda si no es uno de los del desplegable (las
            // promotoras pueden no tener cargo): queda para elegir.
            cargo: esCert ? CARGO_CERTIFICADOR_FIRMAS : esCargoConocido ? cargoPrev : '',
            cargoPersonalizado: '',
            esTitular: esCert ? true : (a.mandatos?.[0]?.esTitular ?? true),
          } as AutoridadElecta;
        });
        nuevos.push({
          id: nuevoId(),
          tipoOrgano,
          denominacionPersonalizada: esCert
            ? LABEL_CERTIFICADORES
            : (lista[0]?.organo?.denominacionPersonalizada || lista[0]?.organo?.denominacion || ''),
          esObligatorio,
          mandatoDesde: '',
          mandatoHasta: '',
          autoridades: await resolverDnisEnLote(personas),
        });
        if (esCert && lista.length > MAX_CERTIFICADORES) {
          toast.error(`La nómina anterior tenía ${lista.length} certificadores: se precargaron los primeros ${MAX_CERTIFICADORES}.`);
        }
      }
      // Los órganos obligatorios que la nómina no tenía se mantienen vacíos.
      const tiposPrecargados = nuevos.map((o) => o.tipoOrgano);
      const faltantes = ORGANOS_OBLIGATORIOS.filter((o) => !tiposPrecargados.includes(o.tipo)).map((o) => ({
        id: nuevoId(), tipoOrgano: o.tipo,
        denominacionPersonalizada: esSeccionCertificadores(o.tipo) ? LABEL_CERTIFICADORES : '',
        esObligatorio: true,
        mandatoDesde: '', mandatoHasta: '', autoridades: [],
      }));
      setOrganos([...nuevos, ...faltantes]);
      toast.success('Autoridades precargadas — revisá los cargos y la condición de cada una.');
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'No se pudieron cargar las autoridades anteriores');
    } finally {
      setPrecargando(null);
    }
  };

  const usarUltimosApoderados = async () => {
    setPrecargando('apod');
    try {
      const data = await cargarNomina();
      if (!data) return;
      const vigentes = data.apoderados.filter((a: any) => a.activo);
      if (vigentes.length === 0) { toast.error('No hay apoderados cargados para reutilizar.'); return; }
      const personas = vigentes.map((a: any) => ({
        ...nuevaPersona(),
        dni: String(a.dni || '').replace(/\D/g, ''),
        nombre: a.nombre || '',
        apellido: a.apellido || '',
        genero: a.genero || '',
      }));
      // Los apoderados no requieren padrón: se usa el mismo chequeo no bloqueante
      // que al tipear el DNI a mano.
      const resueltos = await Promise.all(personas.map(async (p: PersonaDni) => {
        const patch: Partial<PersonaDni> = { estado: 'ok', enPadron: false, dniExisten: false };
        try {
          const r = await api.get(`/padrones/validar-dni-privado/${p.dni}`);
          const d = r.data?.data;
          if (d?.valido) {
            patch.enPadron = true;
            patch.nombre = d.datos?.nombre ? soloLetras(d.datos.nombre) : p.nombre;
            patch.apellido = d.datos?.apellido ? soloLetras(d.datos.apellido) : p.apellido;
            patch.genero = d.datos?.genero || p.genero;
          }
        } catch { /* sigue con los datos de la nómina */ }
        try {
          const c = await documentoService.checkDniPersona(p.dni);
          const dd = c.data?.data;
          if (dd?.frente && dd?.dorso) { patch.dniExisten = true; patch.dniFrentePath = dd.frente.path; patch.dniDorsoPath = dd.dorso.path; }
        } catch { /* sin documento previo */ }
        return { ...p, ...patch };
      }));
      setApoderados(resueltos);
      toast.success('Apoderados precargados — revisá los datos antes de continuar.');
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'No se pudieron cargar los apoderados anteriores');
    } finally {
      setPrecargando(null);
    }
  };

  // ---- Validaciones de paso ----
  const autoridadIncompleta = (a: AutoridadElecta) => personaDniIncompleta(a) || !cargoEfectivo(a);
  const autoridadesInvalidas = organos.flatMap((o) => o.autoridades.filter(autoridadIncompleta));
  const apoderadoIncompleto = (a: PersonaDni) =>
    a.dni.replace(/\D/g, '').length < 7 || !a.nombre.trim() || !a.apellido.trim() || !a.genero ||
    a.estado === 'error' || a.estado === 'loading' || (!a.dniExisten && (!a.dniFrenteFile || !a.dniDorsoFile));
  const apoderadosInvalidos = apoderados.filter(apoderadoIncompleto);
  // La sección de certificadores no se denomina (su nombre es fijo), así que no entra
  // en el chequeo de "ponele un nombre".
  const organosSinNombre = organos.filter(
    (o) => !esSeccionCertificadores(o.tipoOrgano) && o.autoridades.length > 0 && !o.denominacionPersonalizada.trim(),
  );
  // Los órganos obligatorios (y la sección de certificadores) deben tener al menos
  // una persona: el backend rechaza la carga si falta alguno.
  const obligatoriosSinAutoridades = ORGANOS_OBLIGATORIOS.filter(
    (ob) => !organos.some((o) => o.tipoOrgano === ob.tipo && o.autoridades.length > 0),
  );
  // Tope de la sección de certificadores. El botón ya lo frena, pero se revalida acá
  // por si la lista llegó precargada desde la nómina anterior.
  const certificadoresDeMas = organos.some(
    (o) => esSeccionCertificadores(o.tipoOrgano) && o.autoridades.length > MAX_CERTIFICADORES,
  );
  const totalAutoridades = organos.reduce((n, o) => n + o.autoridades.length, 0);

  const errorPasoOrganos = (): string | null => {
    if (obligatoriosSinAutoridades.length > 0) {
      return `Cargá al menos una persona en: ${obligatoriosSinAutoridades.map((o) => o.label).join(', ')}.`;
    }
    if (certificadoresDeMas) return `Podés cargar hasta ${MAX_CERTIFICADORES} certificadores de firmas.`;
    if (organosSinNombre.length > 0) return 'Ponele un nombre a cada órgano con autoridades cargadas.';
    if (autoridadesInvalidas.length > 0) return 'Revisá las autoridades: cargo, DNI válido y foto del DNI (frente y dorso).';
    return null;
  };

  const siguiente = () => {
    if (pasoActual === 0 && !docsCompletos) { toast.error(`Adjuntá los ${DOCS_A_CARGAR.length} documentos para continuar.`); return; }
    if (pasoActual === 1) {
      if (!datosPartido.domicilioLegal.trim()) { toast.error('El domicilio legal del partido es obligatorio.'); return; }
      if (!EMAIL_RE.test(datosPartido.emailOficial.trim())) {
        toast.error('Cargá un email oficial válido: es una de las casillas a las que se avisan las novedades del trámite.');
        return;
      }
    }
    if (pasoActual === 2) {
      const err = errorPasoOrganos();
      if (err) { toast.error(err); return; }
    }
    if (pasoActual === 3) {
      if (apoderados.length === 0) { toast.error('Cargá al menos un apoderado.'); return; }
      if (apoderadosInvalidos.length > 0) { toast.error('Revisá los apoderados: datos completos y foto del DNI (frente y dorso).'); return; }
    }
    setPasoActual((p) => Math.min(p + 1, pasos.length - 1));
  };
  const anterior = () => setPasoActual((p) => Math.max(p - 1, 0));

  // Bloque reutilizable: reutilizar DNI verificado o subir frente/dorso.
  const renderDniDocs = (persona: PersonaDni, onFile: (campo: 'dniFrenteFile' | 'dniDorsoFile', file: File | null) => void) => {
    if (persona.estado !== 'ok') return null;
    return (
      <div className="mt-3 pt-3 border-t">
        {persona.dniExisten ? (
          <p className="text-xs text-green-700">✓ DNI ya cargado y verificado en el sistema — se reutiliza automáticamente.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {(['dniFrenteFile', 'dniDorsoFile'] as const).map((campo, idx) => {
              const file = persona[campo];
              return (
                <div key={campo}>
                  <label className="block text-xs text-gray-500 mb-1">DNI {idx === 0 ? 'Frente' : 'Dorso'} *</label>
                  <div className="flex items-center gap-2">
                    {file && <span className="text-xs text-green-700 truncate max-w-[110px]">✓ {file.name}</span>}
                    <label className="cursor-pointer text-xs font-medium text-green-700 hover:text-green-800">
                      {file ? 'Cambiar' : 'Adjuntar'}
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => onFile(campo, e.target.files?.[0] || null)} />
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const enviar = async () => {
    if (!recon.expedienteId) return;
    // Cada error devuelve al paso donde se corrige (ver `pasos`):
    // 0 Documentos · 1 Datos del partido · 2 Órganos · 3 Apoderados.
    if (!docsCompletos) { toast.error('Faltan documentos obligatorios.'); setPasoActual(0); return; }
    if (!datosPartido.domicilioLegal.trim()) {
      toast.error('El domicilio legal del partido es obligatorio.'); setPasoActual(1); return;
    }
    if (!EMAIL_RE.test(datosPartido.emailOficial.trim())) {
      toast.error('Cargá un email oficial válido para el partido.'); setPasoActual(1); return;
    }
    const errOrganos = errorPasoOrganos();
    if (errOrganos) { toast.error(errOrganos); setPasoActual(2); return; }
    if (apoderados.length === 0 || apoderadosInvalidos.length > 0) {
      toast.error('Revisá los apoderados: datos completos y foto del DNI.'); setPasoActual(3); return;
    }
    setLoading(true);
    try {
      // 1) Reunir TODOS los archivos nuevos (la documentación de la etapa + los DNI que no
      //    reutilizan uno verificado) y subirlos en un solo lote a S3.
      const docArchivos = DOCS_A_CARGAR
        .map((d) => ({ key: `doc:${d.tipo}`, tipo: d.tipo as string, file: docs[d.tipo] }))
        .filter((a): a is { key: string; tipo: string; file: File } => !!a.file);

      const dniArchivos: Array<{ key: string; file: File }> = [];
      const pushDni = (prefijo: string, p: PersonaDni) => {
        if (p.dniExisten) return;
        if (p.dniFrenteFile) dniArchivos.push({ key: `${prefijo}f:${p.key}`, file: p.dniFrenteFile });
        if (p.dniDorsoFile) dniArchivos.push({ key: `${prefijo}d:${p.key}`, file: p.dniDorsoFile });
      };
      organos.forEach((o) => o.autoridades.forEach((a) => pushDni('dni', a)));
      apoderados.forEach((a) => pushDni('apod', a));

      const todos = [...docArchivos.map((a) => ({ key: a.key, file: a.file })), ...dniArchivos];
      setSubiendo({ current: 0, total: todos.length });
      toast.loading('Subiendo documentos...', { id: 'upload-autoridades' });

      const filesMetadata = todos.map((a) => ({ fileName: a.file.name, contentType: a.file.type }));
      const urlsResp = await api.post('/storage/upload-urls', { files: filesMetadata });
      const urls: { url: string; path: string }[] = urlsResp.data.data;
      if (!urls || urls.length !== todos.length) throw new Error('No se recibieron las URLs de subida correctamente');

      let completados = 0;
      const pathPorKey: Record<string, string> = {};
      await Promise.all(
        todos.map(async (a, i) => {
          await fetch(urls[i].url, { method: 'PUT', body: a.file, headers: { 'Content-Type': a.file.type } });
          pathPorKey[a.key] = urls[i].path;
          completados++;
          setSubiendo({ current: completados, total: todos.length });
        }),
      );
      toast.dismiss('upload-autoridades');

      const documentos = docArchivos.map((a) => ({
        tipo: a.tipo, path: pathPorKey[a.key], nombreOriginal: a.file.name, mimeType: a.file.type, tamanio: a.file.size,
      }));

      const organosPayload = organos
        .filter((o) => o.autoridades.length > 0)
        .map((o) => ({
          tipoOrgano: o.tipoOrgano,
          denominacion: o.denominacionPersonalizada || orgLabel(o.tipoOrgano),
          denominacionPersonalizada: o.denominacionPersonalizada || null,
          esObligatorio: o.esObligatorio,
          mandatoDesde: o.mandatoDesde || null,
          mandatoHasta: o.mandatoHasta || null,
          autoridades: o.autoridades.map((a) => ({
            dni: a.dni.replace(/\D/g, ''),
            nombre: a.nombre.trim(),
            apellido: a.apellido.trim(),
            genero: a.genero || 'OTRO',
            cargo: cargoEfectivo(a),
            esTitular: a.esTitular,
            dniFrentePath: a.dniExisten ? a.dniFrentePath : pathPorKey[`dnif:${a.key}`],
            dniDorsoPath: a.dniExisten ? a.dniDorsoPath : pathPorKey[`dnid:${a.key}`],
          })),
        }));

      const apoderadosPayload = apoderados.map((a) => ({
        dni: a.dni.replace(/\D/g, ''),
        nombre: a.nombre.trim(),
        apellido: a.apellido.trim(),
        genero: a.genero || 'OTRO',
        dniFrentePath: a.dniExisten ? a.dniFrentePath : pathPorKey[`apodf:${a.key}`],
        dniDorsoPath: a.dniExisten ? a.dniDorsoPath : pathPorKey[`apodd:${a.key}`],
      }));

      await reconocimientoService.cargarAutoridadesDefinitivas(recon.expedienteId, {
        organos: organosPayload,
        apoderados: apoderadosPayload,
        documentos,
        partido: {
          domicilioLegal: datosPartido.domicilioLegal.trim() || null,
          emailOficial: datosPartido.emailOficial.trim() || null,
          // En un municipal manda SIEMPRE la localidad registrada, no lo que haya
          // quedado en el formulario: define el ámbito del partido y ya salió
          // publicada en el edicto del Art. 10.
          localidad: localidadFija || datosPartido.localidad || null,
          telefonoOficial: datosPartido.telefonoOficial.trim() || null,
          sitioWeb: datosPartido.sitioWeb.trim() || null,
          redesSociales: datosPartido.redesSociales.trim() || null,
          fechaUltimaEleccionInterna: datosPartido.fechaUltimaEleccionInterna || null,
        },
      });

      await queryClient.invalidateQueries({ queryKey: ['mi-expediente'] });
      toast.success('Autoridades definitivas y apoderados enviados');
      navigate('/dashboard', { replace: true });
    } catch (e: any) {
      toast.dismiss('upload-autoridades');
      toast.error(e?.response?.data?.error?.message || e?.message || 'Error al enviar las autoridades', { duration: 8000 });
    } finally {
      setLoading(false);
      setSubiendo(null);
    }
  };

  if (recon.isLoading || !recon.mostrarWizardAutoridades) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-teal-50 to-cyan-50">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-teal-50 to-cyan-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">🏛️</div>
            <div>
              <h1 className="font-bold text-gray-900">Autoridades definitivas del partido</h1>
              <p className="text-sm text-gray-500">Reconocimiento definitivo — {recon.miExpediente?.numero}</p>
            </div>
          </div>
          <button onClick={() => navigate('/dashboard')} className="text-gray-500 hover:text-gray-700 text-sm">← Volver al inicio</button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Stepper */}
        <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
          {pasos.map((titulo, i) => (
            <div key={titulo} className="flex items-center">
              <div
                onClick={() => { if (i <= pasoActual) setPasoActual(i); }}
                className={`flex items-center justify-center w-8 h-8 rounded-full border-2 text-sm font-medium cursor-pointer ${
                  pasoActual > i ? 'bg-green-600 border-green-600 text-white' : pasoActual === i ? 'bg-green-100 border-green-600 text-green-600' : 'border-gray-300 text-gray-400'
                }`}
              >
                {pasoActual > i ? '✓' : i + 1}
              </div>
              <span className={`ml-2 text-xs hidden md:inline ${pasoActual >= i ? 'text-green-600' : 'text-gray-400'}`}>{titulo}</span>
              {i < pasos.length - 1 && <div className={`w-8 md:w-16 h-0.5 mx-2 ${pasoActual > i ? 'bg-green-600' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Paso 0: Documentos de la etapa */}
          {pasoActual === 0 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Documentación de las autoridades electas</h2>
              <p className="text-gray-600 mb-6">
                Adjuntá los {DOCS_A_CARGAR.length} documentos que respaldan la convocatoria y la elección
                de las autoridades partidarias, y la designación de los apoderados (PDF o imagen).
                Todos son obligatorios. Las fichas de afiliación se entregan en papel: esa fila no
                pide archivo.
              </p>
              <div className="space-y-3">
                {DOCS_ETAPA.map((d) => (
                  <div
                    key={d.tipo}
                    className={`border rounded-lg p-4 ${
                      d.sinCarga ? 'border-blue-200 bg-blue-50' : docs[d.tipo] ? 'border-green-300 bg-green-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="font-medium text-gray-800">{d.label}</p>
                        <p className="text-xs text-gray-500">{d.ayuda}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        {!d.sinCarga && docs[d.tipo] && (
                          <span className="text-sm text-green-700 truncate max-w-[160px]">✓ {docs[d.tipo]!.name}</span>
                        )}
                        {d.plantilla && (
                          <button
                            type="button"
                            onClick={() => descargarPlantilla(d.plantilla!.url, d.plantilla!.filename)}
                            className="text-sm font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap"
                          >
                            ⬇ Descargar modelo
                          </button>
                        )}
                        {/* Donde iría "Adjuntar" va la aclaración: esta pieza no se sube. */}
                        {d.sinCarga ? (
                          <span className="text-sm font-medium text-blue-700 whitespace-nowrap">
                            No se adjunta acá
                          </span>
                        ) : (
                          <label className="cursor-pointer text-sm font-medium text-green-700 hover:text-green-800">
                            {docs[d.tipo] ? 'Cambiar' : 'Adjuntar'}
                            <input type="file" accept={acceptParaTipo(d.tipo)} className="hidden" onChange={(e) => setDoc(d.tipo, e.target.files?.[0] || null)} />
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Las especificaciones de imprenta de la ficha: solo es válida impresa así. */}
              <p className="text-xs text-gray-600 mt-4 border-t pt-3">
                <strong>Especificaciones técnicas exigidas para la impresión de la ficha:</strong> 12 cm x 20 cm,
                impresión a un solo color (1/1), sobre papel obra extra blanco de 140/142 gramos.
              </p>
            </div>
          )}

          {/* Paso 1: Datos del partido */}
          {pasoActual === 1 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Datos del partido</h2>
              <p className="text-gray-600 mb-2">
                Completá los datos que faltan del partido para el reconocimiento definitivo.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Los datos que ya declaraste vienen precargados — revisalos y corregí lo que haga falta.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Domicilio legal *</label>
                  <input
                    type="text" value={datosPartido.domicilioLegal}
                    onChange={(e) => setDatosPartido((p) => ({ ...p, domicilioLegal: e.target.value }))}
                    placeholder="Calle, número, piso/depto"
                    className={`w-full px-3 py-2 border rounded-lg text-sm ${!datosPartido.domicilioLegal.trim() ? 'border-red-400' : 'border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Localidad</label>
                  {localidadFija ? (
                    <>
                      <p className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm text-gray-700">
                        {localidadFija}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Es la localidad de su partido municipal, declarada al iniciar el trámite.
                        Si está mal, avise a la Secretaría Electoral: no se puede cambiar desde acá.
                      </p>
                    </>
                  ) : (
                    <select
                      value={datosPartido.localidad}
                      onChange={(e) => setDatosPartido((p) => ({ ...p, localidad: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                    >
                      <option value="">Seleccioná una localidad…</option>
                      {localidades.map((l) => <option key={l.id} value={l.nombre}>{l.nombre}</option>)}
                    </select>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email oficial del partido *</label>
                  <input
                    type="email" value={datosPartido.emailOficial}
                    onChange={(e) => setDatosPartido((p) => ({ ...p, emailOficial: e.target.value }))}
                    placeholder="partido@ejemplo.com"
                    className={`w-full px-3 py-2 border rounded-lg text-sm ${!datosPartido.emailOficial.trim() ? 'border-red-400' : 'border-gray-300'}`}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    A esta casilla se le envíaran los avisos del trámite, además del correo del iniciador del tramite.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono oficial</label>
                  <input type="text" value={datosPartido.telefonoOficial} onChange={(e) => setDatosPartido((p) => ({ ...p, telefonoOficial: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sitio web</label>
                  <input type="text" value={datosPartido.sitioWeb} onChange={(e) => setDatosPartido((p) => ({ ...p, sitioWeb: e.target.value }))} placeholder="https://…" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Redes sociales</label>
                  <input type="text" value={datosPartido.redesSociales} onChange={(e) => setDatosPartido((p) => ({ ...p, redesSociales: e.target.value }))} placeholder="Instagram, Facebook, X…" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de la última elección interna</label>
                  <input type="date" value={datosPartido.fechaUltimaEleccionInterna} onChange={(e) => setDatosPartido((p) => ({ ...p, fechaUltimaEleccionInterna: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
            </div>
          )}

          {/* Paso 2: Órganos y autoridades electas */}
          {pasoActual === 2 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Órganos, autoridades electas y certificadores</h2>
              <p className="text-gray-600 mb-4">
                Cargá las autoridades proclamadas en cada órgano de conducción, indicando su cargo y si
                son titulares o suplentes. Los tres primeros órganos son obligatorios.
              </p>
              <p className="text-gray-600 mb-4">
                Al final hay una sección aparte para los <strong>{LABEL_CERTIFICADORES}</strong>: no es un
                órgano de conducción, son las personas que avalan las fichas de afiliación y las planillas
                de adherentes de todo el partido. Podés cargar hasta {MAX_CERTIFICADORES}, y todas son
                titulares.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800">
                ⚠️ Al enviar el formulario, las <strong>autoridades promotoras</strong> pasarán a la nómina
                histórica del partido (conservando ese título) y quedarán vigentes estas autoridades definitivas.
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 flex flex-wrap items-center justify-between gap-3 text-sm text-blue-800">
                <span>💡 Si muchas de las autoridades promotoras fueron reelectas, podés precargar sus datos.</span>
                <button
                  type="button"
                  onClick={usarUltimasAutoridades}
                  disabled={precargando !== null}
                  className="px-3 py-1.5 rounded-lg border border-blue-400 bg-white text-blue-700 hover:bg-blue-100 disabled:opacity-50 whitespace-nowrap"
                >
                  {precargando === 'aut' ? 'Precargando...' : 'Usar las últimas autoridades'}
                </button>
              </div>

              <div className="space-y-6">
                {organos.map((organo) => {
                  const voc = vocabularioOrgano(organo.tipoOrgano);
                  const esCert = esSeccionCertificadores(organo.tipoOrgano);
                  return (
                  <div key={organo.id} className={`border-2 rounded-xl overflow-hidden ${esCert ? 'border-purple-200' : organo.esObligatorio ? 'border-green-200' : 'border-indigo-200'}`}>
                    <div className={`px-4 py-3 flex items-center justify-between ${esCert ? 'bg-purple-50' : organo.esObligatorio ? 'bg-green-50' : 'bg-indigo-50'}`}>
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{voc.icono}</span>
                        <div>
                          <h3 className="font-bold text-gray-900">{orgLabel(organo.tipoOrgano)}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${esCert ? 'bg-purple-100 text-purple-800' : organo.esObligatorio ? 'bg-green-100 text-green-800' : 'bg-indigo-100 text-indigo-800'}`}>
                            {organo.esObligatorio ? 'Obligatorio' : 'Opcional'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">{voc.contarPersonas(organo.autoridades.length)}</span>
                        {!organo.esObligatorio && (
                          <button type="button" onClick={() => eliminarOrgano(organo.id)} className="ml-2 w-6 h-6 bg-red-100 text-red-600 rounded-full hover:bg-red-200 text-xs">🗑️</button>
                        )}
                      </div>
                    </div>
                    <div className="p-4">
                      {esCert ? (
                        <p className="text-sm text-purple-800 bg-purple-50 border border-purple-100 rounded-lg p-3 mb-4">
                          Quienes avalan las fichas de afiliación y las planillas de adherentes.
                          No es un órgano de conducción: la función alcanza a todo el partido, así que no
                          lleva nombre ni mandato. Hasta {MAX_CERTIFICADORES} personas, todas titulares.
                        </p>
                      ) : (
                      <div className="grid md:grid-cols-3 gap-3 mb-4">
                        <div className="md:col-span-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">{voc.labelNombre}</label>
                          <input
                            type="text"
                            value={organo.denominacionPersonalizada}
                            onChange={(e) => updateOrgano(organo.id, 'denominacionPersonalizada', e.target.value)}
                            placeholder={orgLabel(organo.tipoOrgano)}
                            className={`w-full px-3 py-2 border rounded-lg text-sm ${organo.autoridades.length > 0 && !organo.denominacionPersonalizada.trim() ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Mandato desde</label>
                          <input type="date" value={organo.mandatoDesde} onChange={(e) => updateOrgano(organo.id, 'mandatoDesde', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Mandato hasta</label>
                          <input type="date" value={organo.mandatoHasta} onChange={(e) => updateOrgano(organo.id, 'mandatoHasta', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                        </div>
                      </div>
                      )}

                      <div className="space-y-3">
                        {organo.autoridades.map((a) => (
                          <div key={a.key} className="bg-gray-50 rounded-lg p-3 relative border">
                            <button type="button" onClick={() => eliminarAutoridad(organo.id, a.key)} className="absolute top-2 right-2 w-6 h-6 bg-red-100 text-red-600 rounded-full hover:bg-red-200 text-xs">✕</button>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 pr-6">
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">DNI *</label>
                                <input
                                  type="text" value={a.dni} maxLength={8}
                                  onChange={(e) => updateAutoridad(organo.id, a.key, { dni: e.target.value.replace(/\D/g, ''), estado: 'idle', nombre: '', apellido: '', dniExisten: undefined, dniFrentePath: undefined, dniDorsoPath: undefined })}
                                  onBlur={(e) => validarDniAutoridad(organo.id, a.key, e.target.value)}
                                  className={`w-full px-2 py-1.5 border rounded text-sm ${a.estado === 'error' ? 'border-red-500 bg-red-50' : ''}`}
                                />
                                {a.estado === 'loading' && <span className="text-xs text-blue-600">Validando...</span>}
                                {a.estado === 'ok' && <span className="text-xs text-green-600">✓ DNI válido</span>}
                                {a.estado === 'error' && <span className="text-xs text-red-600">✗ {a.mensaje}</span>}
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Nombre</label>
                                <input type="text" value={a.nombre} readOnly disabled placeholder="Se completa con el DNI" className="w-full px-2 py-1.5 border rounded text-sm bg-gray-100 text-gray-700" />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Apellido</label>
                                <input type="text" value={a.apellido} readOnly disabled placeholder="Se completa con el DNI" className="w-full px-2 py-1.5 border rounded text-sm bg-gray-100 text-gray-700" />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Cargo *</label>
                                {/* En la sección de certificadores el cargo es único: se
                                    muestra fijo en vez de un desplegable de una opción. */}
                                {esCert ? (
                                  <input
                                    type="text" value={LABEL_CARGO_CERTIFICADOR} readOnly disabled
                                    className="w-full px-2 py-1.5 border rounded text-sm bg-gray-100 text-gray-700"
                                  />
                                ) : (
                                  <>
                                    <select
                                      value={a.cargo}
                                      onChange={(e) => updateAutoridad(organo.id, a.key, { cargo: e.target.value, cargoPersonalizado: '' })}
                                      className={`w-full px-2 py-1.5 border rounded text-sm bg-white ${!a.cargo ? 'border-red-400 text-gray-500' : ''}`}
                                    >
                                      <option value="" disabled>Seleccioná un cargo…</option>
                                      {cargosDeOrgano(organo.tipoOrgano).map((c) => (
                                        <option key={c} value={c} disabled={cargoYaExisteEnOrgano(organo.id, c, a.key, a.esTitular)}>
                                          {etiquetaCargo(c)}
                                        </option>
                                      ))}
                                    </select>
                                    {a.cargo === 'OTRO' && (
                                      <input
                                        type="text" value={a.cargoPersonalizado} placeholder="Nombre del cargo"
                                        onChange={(e) => updateAutoridad(organo.id, a.key, { cargoPersonalizado: e.target.value })}
                                        className={`mt-1 w-full px-2 py-1.5 border rounded text-sm ${!a.cargoPersonalizado.trim() ? 'border-red-400' : ''}`}
                                      />
                                    )}
                                  </>
                                )}
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Condición *</label>
                                {/* No existe el certificador suplente: la condición queda fija. */}
                                {esCert ? (
                                  <input
                                    type="text" value="Titular" readOnly disabled
                                    className="w-full px-2 py-1.5 border rounded text-sm bg-gray-100 text-gray-700"
                                  />
                                ) : (
                                  <select
                                    value={a.esTitular ? 'titular' : 'suplente'}
                                    onChange={(e) => {
                                      const esTit = e.target.value === 'titular';
                                      // El presidente no admite suplente: si pasa a
                                      // suplente, se limpia el cargo.
                                      updateAutoridad(organo.id, a.key, {
                                        esTitular: esTit,
                                        ...(!esTit && a.cargo === 'PRESIDENTE' ? { cargo: '' } : {}),
                                      });
                                    }}
                                    className="w-full px-2 py-1.5 border rounded text-sm bg-white"
                                  >
                                    <option value="titular">Titular</option>
                                    <option value="suplente">Suplente</option>
                                  </select>
                                )}
                              </div>
                            </div>
                            {renderDniDocs(a, (campo, file) => updateAutoridad(organo.id, a.key, { [campo]: file } as Partial<AutoridadElecta>))}
                          </div>
                        ))}
                      </div>

                      {/* En certificadores el botón desaparece al llegar al tope. */}
                      {!(esCert && organo.autoridades.length >= MAX_CERTIFICADORES) && (
                        <button type="button" onClick={() => agregarAutoridad(organo.id)} className={`mt-3 text-sm font-medium ${esCert ? 'text-purple-700 hover:text-purple-800' : 'text-green-700 hover:text-green-800'}`}>
                          ＋ {voc.labelAgregarPersona}
                        </button>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>

              <div className="mt-6 border-t pt-4">
                <p className="text-xs font-medium text-gray-500 mb-2">Agregar otro órgano</p>
                <div className="flex flex-wrap gap-2">
                  {ORGANOS_OPCIONALES.map((o) => (
                    <button key={o.tipo} type="button" onClick={() => agregarOrgano(o.tipo)} className="px-3 py-1.5 text-sm rounded-lg border border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                      ＋ {o.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Paso 3: Apoderados */}
          {pasoActual === 3 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Apoderados</h2>
              <p className="text-gray-600 mb-4">Cargá los apoderados designados en el Acta de Designación de Apoderados.</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 flex flex-wrap items-center justify-between gap-3 text-sm text-blue-800">
                <span>💡 Si los apoderados son los mismos que venía teniendo el partido, podés precargarlos.</span>
                <button
                  type="button"
                  onClick={usarUltimosApoderados}
                  disabled={precargando !== null}
                  className="px-3 py-1.5 rounded-lg border border-blue-400 bg-white text-blue-700 hover:bg-blue-100 disabled:opacity-50 whitespace-nowrap"
                >
                  {precargando === 'apod' ? 'Precargando...' : 'Usar los últimos apoderados'}
                </button>
              </div>

              <div className="space-y-3">
                {apoderados.map((a) => (
                  <div key={a.key} className="bg-gray-50 rounded-lg p-3 relative border">
                    <button type="button" onClick={() => eliminarApoderado(a.key)} className="absolute top-2 right-2 w-6 h-6 bg-red-100 text-red-600 rounded-full hover:bg-red-200 text-xs">✕</button>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pr-6">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">DNI *</label>
                        <input
                          type="text" value={a.dni} maxLength={8}
                          onChange={(e) => updateApoderado(a.key, { dni: e.target.value.replace(/\D/g, ''), estado: 'idle', enPadron: undefined, nombre: '', apellido: '', genero: '', dniExisten: undefined, dniFrentePath: undefined, dniDorsoPath: undefined })}
                          onBlur={(e) => chequearDniApoderado(a.key, e.target.value)}
                          className={`w-full px-2 py-1.5 border rounded text-sm ${a.estado === 'error' ? 'border-red-500 bg-red-50' : ''}`}
                        />
                        {a.estado === 'loading' && <span className="text-xs text-blue-600">Verificando...</span>}
                        {a.estado === 'ok' && a.enPadron && <span className="text-xs text-green-600">✓ En el padrón</span>}
                        {a.estado === 'ok' && !a.enPadron && <span className="text-xs text-amber-600">No está en el padrón — cargá los datos</span>}
                        {a.estado === 'error' && <span className="text-xs text-red-600">✗ {a.mensaje}</span>}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Nombre *</label>
                        <input type="text" value={a.nombre} readOnly={a.enPadron} disabled={a.enPadron} onChange={(e) => updateApoderado(a.key, { nombre: soloLetras(e.target.value) })} className={`w-full px-2 py-1.5 border rounded text-sm ${a.enPadron ? 'bg-gray-100 text-gray-700' : ''}`} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Apellido *</label>
                        <input type="text" value={a.apellido} readOnly={a.enPadron} disabled={a.enPadron} onChange={(e) => updateApoderado(a.key, { apellido: soloLetras(e.target.value) })} className={`w-full px-2 py-1.5 border rounded text-sm ${a.enPadron ? 'bg-gray-100 text-gray-700' : ''}`} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Género *</label>
                        {a.enPadron ? (
                          <input type="text" value={generoLabel(a.genero)} readOnly disabled className="w-full px-2 py-1.5 border rounded text-sm bg-gray-100 text-gray-700" />
                        ) : (
                          <select value={a.genero} onChange={(e) => updateApoderado(a.key, { genero: e.target.value })} className={`w-full px-2 py-1.5 border rounded text-sm bg-white ${!a.genero ? 'border-red-400 text-gray-500' : ''}`}>
                            <option value="" disabled>Elegí…</option>
                            {GENEROS_MANUAL.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                          </select>
                        )}
                      </div>
                    </div>
                    {a.estado !== 'error' && a.dni.replace(/\D/g, '').length >= 7 && renderDniDocs({ ...a, estado: 'ok' }, (campo, file) => updateApoderado(a.key, { [campo]: file }))}
                  </div>
                ))}
              </div>

              <button type="button" onClick={agregarApoderado} className="mt-4 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-green-500 hover:text-green-600">
                ＋ Agregar apoderado
              </button>
            </div>
          )}


          {/* Paso 4: Confirmar */}
          {pasoActual === 4 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Revisá y enviá</h2>
              <div className="space-y-4">
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-2">Documentos ({DOCS_A_CARGAR.filter((d) => docs[d.tipo]).length}/{DOCS_A_CARGAR.length})</h3>
                  <ul className="text-sm space-y-1">
                    {DOCS_A_CARGAR.map((d) => (
                      <li key={d.tipo} className={docs[d.tipo] ? 'text-green-700' : 'text-red-600'}>{docs[d.tipo] ? '✓' : '✗'} {d.label}</li>
                    ))}
                    {/* Las fichas no se cuentan como carga: se entregan en papel. Se
                        listan igual, para que el partido no crea que se las olvidó. */}
                    {DOCS_ETAPA.filter((d) => d.sinCarga).map((d) => (
                      <li key={d.tipo} className="text-blue-700">📄 {d.label}</li>
                    ))}
                  </ul>
                </div>
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-2">Datos del partido</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>Domicilio legal: {datosPartido.domicilioLegal || '—'}</li>
                    <li>Localidad: {localidadFija || datosPartido.localidad || '—'}</li>
                    <li>Email oficial: {datosPartido.emailOficial || '—'}</li>
                    <li>Teléfono: {datosPartido.telefonoOficial || '—'}</li>
                    <li>Sitio web: {datosPartido.sitioWeb || '—'}</li>
                    <li>Redes: {datosPartido.redesSociales || '—'}</li>
                    <li>Última elección interna: {datosPartido.fechaUltimaEleccionInterna ? new Date(datosPartido.fechaUltimaEleccionInterna).toLocaleDateString('es-AR') : '—'}</li>
                  </ul>
                </div>
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-2">Órganos y autoridades electas ({totalAutoridades})</h3>
                  <ul className="text-sm space-y-2">
                    {organos.filter((o) => o.autoridades.length > 0).map((o) => (
                      <li key={o.id}>
                        <span className="font-medium text-gray-700">{o.denominacionPersonalizada || orgLabel(o.tipoOrgano)}:</span>{' '}
                        <span className="text-gray-600">
                          {o.autoridades.map((a) => `${a.nombre} ${a.apellido} (${cargoEfectivo(a).toLowerCase()}${a.esTitular ? '' : ', suplente'})`).join(', ')}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-2">Apoderados ({apoderados.length})</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {apoderados.map((a) => <li key={a.key}>{a.nombre} {a.apellido} — DNI {a.dni}</li>)}
                  </ul>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                  ⚠️ Al enviar, las autoridades y apoderados promotores pasan a la nómina histórica del partido.
                </div>
              </div>
              {subiendo && <p className="mt-4 text-sm text-blue-600">Subiendo documentos {subiendo.current}/{subiendo.total}...</p>}
            </div>
          )}

          {/* Navegación */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t">
            <button type="button" onClick={anterior} disabled={pasoActual === 0 || loading} className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-40">
              ← Anterior
            </button>
            {pasoActual < pasos.length - 1 ? (
              <button type="button" onClick={siguiente} className="px-6 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium">Siguiente →</button>
            ) : (
              <button type="button" onClick={enviar} disabled={loading} className="px-6 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium disabled:opacity-60">
                {loading ? 'Enviando...' : 'Enviar autoridades definitivas'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
